import React from "react";
import { Helmet } from "react-helmet-async";

export default function Vae() {
  // Fil d'Ariane (Breadcrumb)
  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.loustaletformation.fr/" },
      { "@type": "ListItem", position: 2, name: "VAE Petite Enfance", item: "https://www.loustaletformation.fr/vae" },
    ],
  };

  // HowTo (étapes de la VAE)
  const jsonLdHowTo = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Comprendre la VAE Petite Enfance",
    description:
      "Les grandes étapes d’une VAE : recevabilité, dossier d’expérience, préparation à l’oral et passage devant le jury.",
    estimatedCost: [
      { "@type": "MonetaryAmount", currency: "EUR", value: "2000", name: "Accompagnement intensif (~30h)" },
      { "@type": "MonetaryAmount", currency: "EUR", value: "0", name: "Autodidacte (ressources en ligne / livres)" },
      { "@type": "MonetaryAmount", currency: "EUR", value: "15-35 / mois", name: "Préparation mixte Loustalet Formation" },
    ],
    totalTime: "P10M", // 10 à 12 mois en moyenne
    step: [
      {
        "@type": "HowToStep",
        name: "Vérifier l'éligibilité",
        text:
          "Plus de seuil minimum strict d'expérience ; plus vous êtes à l'aise avec la prise en charge des enfants, plus le parcours sera facilité.",
      },
      {
        "@type": "HowToStep",
        name: "Déposer le Livret 1 (recevabilité)",
        text:
          "Constituer le dossier administratif (attestations, diplômes, etc.) et l’envoyer à l’organisme certificateur.",
      },
      {
        "@type": "HowToStep",
        name: "Rédiger le Livret 2 (expérience)",
        text:
          "Décrire vos activités, rassembler les preuves et aligner avec le référentiel du diplôme.",
      },
      {
        "@type": "HowToStep",
        name: "Se préparer à l’oral du jury",
        text:
          "Simulations d’entretien, structuration du discours (storytelling, timing), gestion du stress et des questions sensibles.",
      },
      {
        "@type": "HowToStep",
        name: "Passer devant le jury",
        text:
          "Entretien et décision : validation totale, partielle (avec compléments) ou non ; plan d’actions si nécessaire.",
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>VAE Petite Enfance : méthode, financement, calendrier | L’Oustalet Formation</title>
        <meta
          name="description"
          content="Tout comprendre de la VAE Petite Enfance : éligibilité, étapes (L1/L2), jury, coûts et financements (CPF). Accompagnement L’Oustalet Formation."
        />
        <link rel="canonical" href="https://www.loustaletformation.fr/vae" />

        {/* Open Graph */}
        <meta property="og:title" content="VAE Petite Enfance : méthode, financement, calendrier" />
        <meta
          property="og:description"
          content="Éligibilité, étapes, jury, coûts & financements. Accompagnement personnalisé L’Oustalet Formation."
        />
        <meta property="og:url" content="https://www.loustaletformation.fr/vae" />
        <meta property="og:type" content="website" />

        {/* Données structurées */}
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdHowTo)}</script>
      </Helmet>

      <section className="px-6 py-10 max-w-4xl mx-auto">
        {/* Fil d’Ariane */}
        <nav className="text-sm text-gray-500 mb-6" aria-label="Fil d’Ariane">
          <a href="/" className="underline">Accueil</a> <span className="mx-2">›</span> <span>VAE Petite Enfance</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-bold text-rose-600 mb-4">
          VAE Petite Enfance : comprendre le parcours et réussir votre validation
        </h1>
        <p className="text-lg text-gray-700 mb-6">
          La VAE (Validation des Acquis de l’Expérience) vous permet d’obtenir un diplôme en valorisant votre expérience
          professionnelle. Voici l’essentiel pour bien démarrer : éligibilité, étapes, calendrier et financements.
        </p>

        {/* Liens d’ancre rapides */}
        <div className="flex flex-wrap gap-3 mb-8">
          <a href="#eligibilite" className="underline text-rose-700">Éligibilité</a>
          <a href="#etapes" className="underline text-rose-700">Étapes</a>
          <a href="#financement" className="underline text-rose-700">Financements</a>
          <a href="#calendrier" className="underline text-rose-700">Calendrier</a>
          <a href="#ressources" className="underline text-rose-700">Ressources</a>
          <a href="#faq" className="underline text-rose-700">FAQ</a>
        </div>

        {/* Éligibilité */}
        <h2 id="eligibilite" className="text-2xl font-semibold mt-10 mb-3">Êtes-vous éligible ?</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li>
            Plus besoin d’un minimum d’expérience mais plus vous êtes à l’aise avec la prise en charge des enfants, plus le parcours sera facilité :)
          </li>
          <li>
            Vos missions passées ou actuelles ne recouvrent pas entièrement les activités du référentiel (soins, hygiène, sécurité, développement de l’enfant, relation aux familles…) il vous faudra acquérir les connaissances manquantes (grâce à notre site !).
          </li>
          <li>
            Il vous sera demandé de fournir divers documents : attestations de travail ou de stage, diplômes déjà obtenus, etc.
          </li>
        </ul>

        {/* Étapes */}
        <h2 id="etapes" className="text-2xl font-semibold mt-10 mb-3">Les grandes étapes d’une VAE</h2>
        <ol className="list-decimal pl-6 space-y-2 text-gray-700">
          <li>
            <strong>Livret 1 (recevabilité)</strong> : vérification de l’adéquation entre votre expérience et le diplôme.
          </li>
          <li>
            <strong>Livret 2</strong> : description argumentée de vos activités et mise en relation avec le référentiel.
          </li>
          <li>
            <strong>Préparation à l’oral du jury</strong> : structurer votre discours, valoriser vos situations-clés et s’entraîner.
          </li>
          <li>
            <strong>Jury</strong> : entretien, décision (validation totale, partielle ou non) et, si besoin, compléments à réaliser.
          </li>
        </ol>

        {/* Financements */}
        <h2 id="financement" className="text-2xl font-semibold mt-10 mb-3">Financements & coûts</h2>
        <div className="text-gray-700 space-y-2">
          <p>
            Selon votre situation, plusieurs dispositifs existent :
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              Une préparation avec un centre de formation type <em>ExcellenceVAE</em> qui propose 30h d’accompagnement pour un tarif
              avoisinant <strong>2000€</strong>.
            </li>
            <li>
              Une préparation <strong>100% autonome</strong> avec l’achat de livres de préparation à la VAE.
            </li>
            <li>
              Une préparation mixte avec Loustalet Formation, en ligne, avec cours, vidéos, QCM d’entraînements et possibilité de réserver des rdv individuels pour les moments clefs de votre parcours de VAE si besoin uniquement. Tarif : entre 15€ et 35€ par mois selon le format choisi.
            </li>
          </ul>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <a href="/tarifs" className="inline-block px-5 py-3 rounded-2xl bg-rose-600 text-white font-semibold shadow">
            Voir nos tarifs
          </a>
          <a href="/vae-auxiliaire-de-puericulture" className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold">
            VAE Auxiliaire de puériculture (DEAP)
          </a>
          <a href="/faq" className="inline-block px-5 py-3 rounded-2xl border border-gray-300 text-gray-700 font-semibold">
            Consulter la FAQ
          </a>
        </div>

        {/* Calendrier */}
        <h2 id="calendrier" className="text-2xl font-semibold mt-10 mb-3">Calendrier & durée</h2>
        <p className="text-gray-700">
          La durée varie selon votre disponibilité et vos acquis. Comptez généralement <strong>10 à 12 mois</strong> pour être devant le jury
          et mettre en avant vos progrès et vos connaissances.
        </p>

        {/* Ressources */}
        <h2 id="ressources" className="text-2xl font-semibold mt-10 mb-3">Ressources & bonnes pratiques</h2>
        <ul className="list-disc pl-6 space-y-2 text-gray-700">
          <li>
           Lisez les intitulés de chaque bloc de compétences attendues pour essayer de visualiser dans votre quotidien à quoi cela correspond.
          </li>
          <li>
            Cartographier vos expériences : situations significatives, responsabilités, résultats.
          </li>
          <li>
            Aligner vos preuves avec les compétences attendues du diplôme.
          </li>
          <li>
            Rédiger au “je” et illustrer par des exemples concrets (avant → action → résultat).
          </li>
          <li>
            Anticiper l’oral : storytelling, timing, réponses aux questions sensibles.
          </li>
          <li>
            Faites un maximum de QCM pour apprendre de façon ludique et prendre confiance en vous !
          </li>
        </ul>


        {/* Lien vers la FAQ */}
<h2 className="text-2xl font-semibold mb-3">FAQ</h2>
<p className="text-gray-700 mb-4">
  Retrouvez toutes les réponses à vos questions sur la VAE d’auxiliaire de puériculture,
  les financements, les blocs de compétences et plus encore.
</p>
<a
  href="/faq"
  className="inline-block px-5 py-3 rounded-2xl border border-rose-600 text-rose-700 font-semibold hover:bg-rose-50 transition"
>
  Consulter la FAQ complète
</a>
      </section>
    </>
  );
}